
// src/routes/AppRoutes.js
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useUser } from '../UserContext';

// Pages
import AboutPage from '../pages/AboutPage';
import AllMetricsPage from '../pages/AllMetricsPage';
import DefinitionPage from '../pages/DefinitionPage';
import MetricsVisualisationsPage from '../pages/MetricsVisualisationsPage';
import MarketContextPage from '../pages/MarketContextPage';
import LandingPage from '../pages/LandingPage';
import DailyBriefPage from '../pages/DailyBriefPage';

// Lazy-loaded auth / upgrade forms
const SignupForm = lazy(() => import('../components/SignupForm'));
const LoginForm = lazy(() => import('../components/LoginForm'));
const UpgradeForm = lazy(() => import('../components/UpgradeForm'));

// ----------------------
// Protected route wrapper
// ----------------------
const ProtectedRoute = ({ isLoggedIn, userPlanTier, children }) => {
  if (!isLoggedIn) return <Navigate to="/signup" replace />;
  if (!userPlanTier) return <Navigate to="/subscribe" replace />;
  return children;
};

const AppRoutes = ({
  // auth
  isLoggedIn,
  setIsLoggedIn,

  // layout
  PageLayout,

  // selection
  selectedExchange,
  setSelectedExchange,
  selectedCompany,
  setSelectedCompany,
  selectedQuarter,
  setSelectedQuarter,
  companies,
  setCompanies,
  quarters,
  setQuarters,

  // metrics
  incomeMetrics,
  balanceMetrics,
  cashflowMetrics,
  metricsData,

  stockSymbol,
  priceDetails,
  sharePrice,
  sharePriceTimestamp,

  historicalSeries,
  visualQuarters,
  setVisualQuarters,

  availableQuarters,
  selectedMetricQuarter,
  setSelectedMetricQuarter,

  refreshMetrics,
  loading,
  errorMessage,

  // config
  companyOptions,
  userEmail,
  backendBaseUrl,
  pingBackend,
}) => {
  const { userPlanTier } = useUser();

  // ✅ Centralised controls wiring (used everywhere showControls=true)
  const commonControlsProps = {
    selectedExchange,
    setSelectedExchange,
    selectedCompany,
    setSelectedCompany,
    selectedQuarter,
    setSelectedQuarter,
    refreshMetrics,
    errorMessage,
    companyOptions,
    quarters,
    setCompanies,
    setQuarters,
    availableQuarters,
    selectedMetricQuarter,
    setSelectedMetricQuarter,
    isLoggedIn,
    loading,
    pingBackend,
  };

  const commonSidebarProps = {
    companyName: selectedCompany,
    sharePrice,
    sharePriceTimestamp,
    stockSymbol,
    change: priceDetails?.['Change'],
    changePercent: priceDetails?.['Change %'],
    marketCap: priceDetails?.['Market Cap'],
    high52Week: priceDetails?.['52-Week High'],
    low52Week: priceDetails?.['52-Week Low'],
    beta: priceDetails?.['Beta'],
    trailingPE: priceDetails?.['Trailing P/E'],
    forwardPE: priceDetails?.['Forward P/E'],
    pegRatio: priceDetails?.['PEG Ratio'],
    epsTTM: priceDetails?.['EPS (TTM)'],
    revenueGrowth: priceDetails?.['Revenue Growth'],
    dividendYield: priceDetails?.['Dividend Yield'],
    dataRefreshed: priceDetails?.['Data Refreshed'],
  };

  return (
    <Routes>
      {/* Default */}
      <Route path="/" element={<LandingPage isLoggedIn={isLoggedIn} />} />

      {/* Daily Brief */}
      <Route
        path="/daily"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn}>
            <DailyBriefPage />
          </ProtectedRoute>
        }
      />

      {/* About */}
      <Route
        path="/about"
        element={
          <PageLayout sidebarProps={commonSidebarProps}>
            <AboutPage
              selectedCompany={selectedCompany}
              selectedQuarter={selectedQuarter}
              metricsData={metricsData}
            />
          </PageLayout>
        }
      />

      {/* Definitions */}
      <Route
        path="/definition"
        element={
          <PageLayout sidebarProps={commonSidebarProps}>
            <DefinitionPage
              selectedCompany={selectedCompany}
              selectedQuarter={selectedQuarter}
              metricsData={metricsData}
            />
          </PageLayout>
        }
      />

      {/* Metrics */}
      <Route
        path="/metrics"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} userPlanTier={userPlanTier}>
            <PageLayout
              showControls
              sidebarProps={commonSidebarProps}
              controlsProps={commonControlsProps}
            >
              <AllMetricsPage
                selectedCompany={selectedCompany}
                stockSymbol={stockSymbol}
                selectedQuarter={selectedQuarter}
                incomeMetrics={incomeMetrics}
                balanceMetrics={balanceMetrics}
                cashflowMetrics={cashflowMetrics}
                efficiencyMetrics={metricsData?.efficiency ?? {}}
                leverageMetrics={metricsData?.leverage ?? {}}
                profitabilityMetrics={metricsData?.profitability ?? {}}
                valuationMetrics={metricsData?.valuation ?? {}}
                earningsQualityMetrics={metricsData?.earningsQuality ?? {}}
              />
            </PageLayout>
          </ProtectedRoute>
        }
      />

      {/* Visualisations */}
      <Route
        path="/metric-visualisations"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} userPlanTier={userPlanTier}>
            <PageLayout
              showControls
              sidebarProps={commonSidebarProps}
              controlsProps={commonControlsProps}
            >
              <MetricsVisualisationsPage
                selectedCompany={selectedCompany}
                historicalSeries={historicalSeries}
                visualQuarters={visualQuarters}
                setVisualQuarters={setVisualQuarters}
              />
            </PageLayout>
          </ProtectedRoute>
        }
      />

      {/* Market Context */}
      <Route
        path="/market-context"
        element={
          <ProtectedRoute isLoggedIn={isLoggedIn} userPlanTier={userPlanTier}>
            <PageLayout sidebarProps={commonSidebarProps}>
              <MarketContextPage />
            </PageLayout>
          </ProtectedRoute>
        }
      />

      {/* Signup */}
      <Route
        path="/signup"
        element={
          <main className="max-w-3xl mx-auto px-4 py-8">
            <Suspense fallback={<div>Loading...</div>}>
              <SignupForm
                backendBaseUrl={backendBaseUrl}
                setIsLoggedIn={setIsLoggedIn}
              />
            </Suspense>
          </main>
        }
      />

      {/* Login */}
      <Route
        path="/login"
        element={
          <main className="max-w-3xl mx-auto px-4 py-8">
            <Suspense fallback={<div>Loading...</div>}>
              <LoginForm
                setIsLoggedIn={setIsLoggedIn}
                backendBaseUrl={backendBaseUrl}
              />
            </Suspense>
          </main>
        }
      />

      {/* Subscribe */}
      <Route
        path="/subscribe"
        element={
          <main className="max-w-3xl mx-auto px-4 py-8">
            <Suspense fallback={<div>Loading...</div>}>
              <UpgradeForm
                userEmail={userEmail}
                backendBaseUrl={backendBaseUrl}
              />
            </Suspense>
          </main>
        }
      />
    </Routes>
  );
};

export default AppRoutes;
